import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as AuthActions from './auth.actions';
import * as AuthSelectors from './auth.selectors';
import { AuthService } from '../auth.service';
import { AdminUserService, AdminUserWithPreferences } from '../../shared/services/admin-user.service';
import { PreferencesService } from '../../shared/services/preferences/preferences.service';
import { lastTenantIdFromLogin } from '../../shared/utils/tenant-storage.util';
import { catchError, EMPTY, filter, from, map, Observable, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { Auth } from '@angular/fire/auth';
import { signOut } from 'firebase/auth';

import { Router } from '@angular/router';
import { MixpanelService } from 'src/app/shared/services/mixpanel.service';
import { MatDialog } from '@angular/material/dialog';

import { GuardStatusService } from 'src/app/shared/services/guard-status.service';
import { SessionManagementService } from 'src/app/services/session-management.service';
import { RoutePaths } from 'src/app/shared/api/types/enums';

@Injectable()
export class AuthEffects {
  constructor(
    private actions: Actions,
    private authService: AuthService,
    private adminUserService: AdminUserService,
    private store: Store,
    private auth: Auth,
    public router: Router,
    private mixpanelService: MixpanelService,
    private dialog: MatDialog,
    private guardStatusService: GuardStatusService,
    private sessionManagementService: SessionManagementService,
    private preferencesService: PreferencesService
  ) {}

  public readonly authLogin = createEffect(() => {
    return this.actions.pipe(
      ofType(AuthActions.loginStart),
      switchMap((action) => {
        return from(
          this.authService.signInWithEmailAndPassword(
            action.email,
            action.password,
            action.persistentLogin
          )
        ).pipe(
          catchError((error) => {
            return of(AuthActions.loginError({ error }));
          })
        );
      })
    );
  });

  public readonly autoLogin = createEffect(() => {
    return this.actions.pipe(
      ofType(AuthActions.autoLogin),
      switchMap((action) => {
        return this.store
          .select(AuthSelectors.selectActiveTenant)
          .pipe(
            take(1),
            filter((activeTenant) => !activeTenant),
            map(() => AuthActions.login({ user: action.user }))
          );
      })
    );
  });

  public readonly setTenants = createEffect(() => {
    return this.actions.pipe(
      ofType(AuthActions.login),
      switchMap((action) => {
        const tenantsQuery = this.adminUserService.getTenants(action.user);

        if (!tenantsQuery) {
          return of(AuthActions.logoutStart());
        }

        return tenantsQuery.valueChanges.pipe(
          // CRITICAL: Apollo 4 emits { loading: true, data: undefined } first, then actual data
          // Filter waits for the actual data before proceeding
          filter((result) => !result.loading && !!result.data),
          map((result) => result.data.adminUser),
          map((loginUser: AdminUserWithPreferences) => {
            // NBO-2516: the GLOBAL preferences ride this query. They go to PreferencesService and
            // never into the auth slice — the store would hold a stale second copy of them.
            const { preferences, ...adminUser } = loginUser;
            this.store.dispatch(AuthActions.setUserId({ userId: adminUser.id }));
            if (adminUser && adminUser.tenants && adminUser.tenants.items.length != 0) {
              this.preferencesService.applyLogin(adminUser, preferences);
              this.mixpanelService.identify(adminUser.email);
              this.mixpanelService.setPeople({ $email: adminUser.email });
              this.mixpanelService.trackEvent('sign_in');
              // Compare the PATH, not Router.url, which carries the query
              // string: on /sign-in?<anything> the old equality failed and a
              // successful login left an authenticated user looking at the
              // sign-in form. Same class of bug as the authMode derivation
              // this replaced in SignInComponent (NBO-2387).
              const path = this.router.url.split(/[?#]/)[0];
              if (path === '/' + RoutePaths.SignIn || path === '/' + RoutePaths.MFA) {
                this.router.navigate(['/' + RoutePaths.Home]);
              }

              return AuthActions.setTenants({
                tenants: adminUser.tenants.items,
                adminUser,
                lastTenantId: lastTenantIdFromLogin(preferences)
              });
            } else {
              return AuthActions.logoutStart();
            }
          }),
          catchError((error) => of(AuthActions.loginError({ error })))
        );
      })
    );
  });

  public readonly authLogout = createEffect(() => {
    return this.actions.pipe(
      ofType(AuthActions.logoutStart),
      withLatestFrom(this.guardStatusService.getGuardStatus()),
      switchMap(([action, guardActive]) => {
        if (guardActive) {
          this.router.navigate([], { queryParams: { action: 'logout' }, queryParamsHandling: 'merge' });
          return EMPTY;
        } else {
          return from(signOut(this.auth)).pipe(
            map(() => {
              //this.authService.refreshToken();
              this.sessionManagementService.stopActivityTracking();
              if (localStorage['persistentLogin']) {
                localStorage.removeItem('persistentLogin');
              }
              localStorage.removeItem('lastActivityTime');
              // app_language_last_used is kept: the sign-in page renders in the language just used.
              this.router.navigate(['sign-in']);

              if (this.dialog) {
                this.dialog.closeAll();
              }
              return AuthActions.logout();
            })
          );
        }
      })
    );
  });
}
